import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import SelectComponent from "./SelectComponent";
import { getScoreboardTypesService } from "../../services/commonService";
import {
  SCOREBOARD_TYPE_AUTOMATIC,
  SCOREBOARD_TYPE_AUTOMATIC_BURNT,
} from "../../pages/event/forms/common-forms/sport-form-constants";

// Réplica exacta de la lógica ya usada (y probada) en el flujo de clonación
// (components/select/scoreboard-type-api-select.jsx): colapsa las variantes
// "AUTOMATIC-*" en un único "AUTOMATIC" para el desplegable, y calcula
// onlyAutomaticBurnt con el mismo criterio que ya usaba GraphQL, para no cambiar el
// comportamiento de `sport-form.jsx` (que sigue leyendo
// `onLoadQuery(data => handleScoreboardTypes(data?.scoreboardTypes))`).
const ScoreboardTypeSelect = ({
  id = "scoreboardType",
  name = "scoreboardType",
  contractId,
  eventTypeId,
  roomTemplateId,
  label,
  value,
  handleChange,
  onLoadQuery,
  disabled = false,
}) => {
  const [scoreboardTypes, setScoreboardTypes] = useState([]);
  const previousDependencies = useRef({ contractId, eventTypeId, roomTemplateId });

  useEffect(
    () => {
      if (!contractId || !eventTypeId || !roomTemplateId) {
        setScoreboardTypes([]);
        return;
      }
      getScoreboardTypesService(contractId, eventTypeId, data => {
        const filteredScoreboardTypes = (data || [])
          .map(item => (item.startsWith(SCOREBOARD_TYPE_AUTOMATIC) ? SCOREBOARD_TYPE_AUTOMATIC : item))
          .filter((item, index, self) => self.indexOf(item) === index);
        setScoreboardTypes(filteredScoreboardTypes);

        if (onLoadQuery) {
          const automaticTypes = (data || []).filter(type => type === SCOREBOARD_TYPE_AUTOMATIC);
          onLoadQuery({
            scoreboardTypes: {
              types: filteredScoreboardTypes,
              onlyAutomaticBurnt: automaticTypes?.length === 1 && automaticTypes[0] === SCOREBOARD_TYPE_AUTOMATIC_BURNT,
            },
          });
        }
      });
    },
    // onLoadQuery llega como función anónima nueva en cada render de sport-form.jsx;
    // si se incluyera en las deps, esto refetchearía en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractId, eventTypeId, roomTemplateId],
  );

  // Igual que la versión GraphQL: se limpia el tipo de marcador seleccionado si cambia
  // cualquiera de las 3 dependencias.
  useEffect(
    () => {
      const previous = previousDependencies.current;
      if (
        contractId !== previous.contractId ||
        eventTypeId !== previous.eventTypeId ||
        roomTemplateId !== previous.roomTemplateId
      ) {
        previousDependencies.current = { contractId, eventTypeId, roomTemplateId };
        handleChange({ target: { value: null } });
      }
    },
    [contractId, eventTypeId, roomTemplateId, handleChange],
  );

  return (
    <SelectComponent
      disabled={disabled || !contractId || !eventTypeId || !roomTemplateId}
      id={id}
      name={name}
      label={label}
      value={value}
      handleChange={handleChange}
      elements={scoreboardTypes.map(type => ({ type }))}
      getValueFromElement={item => item.type}
      getLabelFromElement={item => <FormattedMessage id={`event.scoreboardType.in.${item.type}`} />}
      itemKey="type"
      itemValue="type"
      itemLabel="type"
    />
  );
};

ScoreboardTypeSelect.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  contractId: PropTypes.string,
  eventTypeId: PropTypes.string,
  roomTemplateId: PropTypes.string,
  value: PropTypes.string,
  handleChange: PropTypes.func,
  label: PropTypes.string,
  /**
   * Callback tras cargar los tipos, con la misma forma que producía la query GraphQL
   * ({ scoreboardTypes: { types, onlyAutomaticBurnt } }), para no tocar sport-form.jsx.
   */
  onLoadQuery: PropTypes.func,
  disabled: PropTypes.bool,
};

export default ScoreboardTypeSelect;
